import React, { useState, useEffect, useRef } from "react";

import * as am4core from "@amcharts/amcharts4/core";
import ContentCard from "../../common/template/contentCard";
import ContentCardBody from "../../common/template/contentCardBody";
import FormatUtils from "../../common/formatUtils/FormatUtils";
import { adicionarPontosMilhar } from "../../utils/adicionarPontosMilhar.js";
import { montarGraficoClusteredColumnAnual } from "../../common/graficos/montarGraficoClusteredColumnAnual.js";
import { montarGraficoClusteredColumnMensal } from "../../common/graficos/montarGraficoClusteredColumnMensal.js";

export function GraficoRetornoFinanceiro(props) {
    const cores = [am4core.color("#0084ff"), am4core.color("#fdff6a"), am4core.color("#f49e13"), am4core.color("#12ffb8"), am4core.color("#ff5e76"), am4core.color("#00d5ff")];

    const contentCardRef = useRef(null);
    const [graficoAnaliseRetornoFinanceiroPercentualMensal, setGraficoAnaliseRetornoFinanceiroPercentualMensal] = useState(null);
    const [graficoAnaliseRetornoFinanceiroValorAnual, setGaficoAnaliseRetornoFinanceiroValorAnual] = useState(null);

    useEffect(() => {
        atualizarListaGraficoRetornoFinanceiro();
        return desmontarGrafico;
    }, [props.listaGraficoGeracaoEconomia, props.listaGeracaoContrato]);

    function desmontarGrafico() {
        if (graficoAnaliseRetornoFinanceiroPercentualMensal) graficoAnaliseRetornoFinanceiroPercentualMensal.dispose();
        if (graficoAnaliseRetornoFinanceiroValorAnual) graficoAnaliseRetornoFinanceiroValorAnual.dispose();
    }

    function atualizarListaGraficoRetornoFinanceiro() {
        const graficoAnaliseRetornoFinanceiroPercentualMensalAtualizado = montarGraficoClusteredColumnMensal({
            state: { cores },
            component: graficoAnaliseRetornoFinanceiroPercentualMensal,
            nome: "graficoAnaliseRetornoFinanceiroPercentualMensal",
            dados: props.listaGraficoGeracaoEconomia.map((item) => ({
                mes: item.mes,
                anos: item.anos.map((tipo) => {
                    const investimentoAno = parseFloat((props.listaGeracaoContrato.find((geracao) => geracao.ano == tipo.ano) || {}).valor_investido) || 0;
                    return {
                        ano: tipo.ano,
                        valor: tipo.valor > 0 && investimentoAno > 0 ? (parseFloat(tipo.valor) * 100) / investimentoAno : 0,
                    };
                }),
            })),
            fontColor: props.fontColor,
            titulo: "",
            prefixo: "",
            sufixo: "%",
        });

        setGraficoAnaliseRetornoFinanceiroPercentualMensal(graficoAnaliseRetornoFinanceiroPercentualMensalAtualizado);
        //cria uma lista com o retorno financeiro, o valor inverstido no ano e o ano em si
        // [{
        //     agrupador: "Retorno"
        //     ano: 2022
        //     valor: 96692.82767
        //     valorInvestidoAno: 1418658
        // }]

        let listaEconomiaAnual = [];

        props.listaGraficoGeracaoEconomia.forEach((item) => {
            item.anos.forEach((ano) => {
                const anoJaEstaNaLista = listaEconomiaAnual.find((retorno) => retorno.ano == ano.ano);
                if (anoJaEstaNaLista) {
                    anoJaEstaNaLista.valor += parseFloat(ano.valor);
                } else {
                    const geracao = props.listaGeracaoContrato.find((geracao) => geracao.ano == ano.ano) || {};
                    const competencia = geracao.competencia_inicio
                        ? String(geracao.competencia_inicio).substring(4, 6) + "/" + String(geracao.competencia_inicio).substring(0, 4)
                        : "";
                    const competenciaAmpliacao = geracao.competencia_ampliacao
                        ? String(geracao.competencia_ampliacao).substring(4, 6) + "/" + String(geracao.competencia_ampliacao).substring(0, 4)
                        : "";
                    const valorAmpliacao = parseFloat(geracao.valor_ampliacao) || 0;
                    const investimento = parseFloat(geracao.valor_investido) || 0;

                    listaEconomiaAnual.push({
                        agrupador: "Retorno",
                        ano: ano.ano,
                        valor: parseFloat(ano.valor),
                        valorInvestidoAno: investimento,
                        competencia,
                        competenciaAmpliacao,
                        valorAmpliacao,
                        anoInicioGeracao: props.listaGeracaoContrato[0] && props.listaGeracaoContrato[0].ano,
                    });
                }
            });
        });

        let economiaTotal = 0;
        const listaRetornoFinanceiroAnual = [];

        listaEconomiaAnual
            .sort((a, b) => a.ano - b.ano)
            .forEach((retorno, i) => {
                economiaTotal += retorno.valor;
                if ((retorno.anoInicioGeracao < retorno.ano && i == 0) || retorno.ano == retorno.anoInicioGeracao) {
                    const anoString = `Valor Investido Inicial ${retorno.competencia} = [bold]R$ ${adicionarPontosMilhar(retorno.valorInvestidoAno)}[/]\nValor Economizado em ${
                        retorno.ano
                    } = [bold]R$${adicionarPontosMilhar(retorno.valor)}[/]\nValor Resultante em ${retorno.ano} = [bold]${
                        economiaTotal - retorno.valorInvestidoAno < 0 ? " - " : ""
                    } R$${adicionarPontosMilhar(economiaTotal - retorno.valorInvestidoAno).replace("-", "")}[/]\n(Desc. Economias Iniciais)`;

                    listaRetornoFinanceiroAnual.push({
                        ...retorno,
                        ano: anoString,
                        tooltip: anoString,
                        valor: economiaTotal - retorno.valorInvestidoAno, // valorInvestidoAno já tem acumulada as ampliações
                    });
                } else {
                    const textoAmpliacao = retorno.competenciaAmpliacao
                        ? `\nAmpliação (invest. R$ ${adicionarPontosMilhar(retorno.valorAmpliacao)} em ${
                              retorno.competenciaAmpliacao
                          })\n(Acresc. Novo Invest. e Desc. Economias do Ano)`
                        : "";
                    const textoAno = `Valor Resultante em ${retorno.ano} = [bold]${adicionarPontosMilhar(economiaTotal - retorno.valorInvestidoAno)}[/]`;
                    const tooltipTexto = `Valor Economizado em ${retorno.ano}: [bold]${adicionarPontosMilhar(retorno.valor)}[/]\n${textoAno}${textoAmpliacao}`;

                    listaRetornoFinanceiroAnual.push({
                        ...retorno,
                        ano: `${retorno.ano}${textoAmpliacao}`,
                        tooltip: tooltipTexto,
                        valor: economiaTotal - retorno.valorInvestidoAno, // valorInvestidoAno já tem acumulada as ampliações
                    });
                }
            });
        const tituloGraficoAnaliseRetornoFinanceiroValorAnualAtualizado = montarGraficoClusteredColumnAnual({
            state: {
                cores: [am4core.color("#0084ff"), am4core.color("#fdff6a"), am4core.color("#f49e13"), am4core.color("#12ffb8"), am4core.color("#ff5e76"), am4core.color("#00d5ff")],
            },
            component: graficoAnaliseRetornoFinanceiroValorAnual,
            nome: "graficoAnaliseRetornoFinanceiroValorAnual",
            dados: [{ anos: [...listaRetornoFinanceiroAnual] }],
            titulo: "",
            prefixo: "R$",
            sufixo: "",
            negativo: true,
            ordenar: false,
            wrap: false,
            labelPersonalizado: true,
            fontColor: props.fontColor,
            bulletLabelDy: props.bulletLabelDy,
        });
        setGaficoAnaliseRetornoFinanceiroValorAnual(tituloGraficoAnaliseRetornoFinanceiroValorAnualAtualizado);
    }

    //-----------------------------------------------------------------------------------------------------------------------------------------------------------
    let economia = 0;
    props.listaGraficoGeracaoEconomia.forEach((item, i) => {
        item.anos.forEach((ano, i) => {
            economia += parseFloat(ano.valor);
        });
    });
    const totalInvestido = parseFloat((props.listaGeracaoContrato[props.listaGeracaoContrato.length - 1] || {}).valor_investido) || 0; //a ultima tem o acumulado de todos os anos
    let retornoFinanceiro = economia > 0 && totalInvestido > 0 ? (economia * 100) / totalInvestido : 0;

    return (
        <div ref={contentCardRef}>
            <ContentCard
                style={{
                    color: "#fff",
                    padding: 8,
                }}
            >
                {/* <ContentCardBody
                    style={{
                        backgroundColor: props.backgroundColor || "#30303d",
                        textAlign: "center",
                    }}
                >
                    <div id="tituloGraficoAnaliseRetornoFinanceiroPercentualMensal" style={{ color: props.fontColor || "#fff", textAlign: "center" }}>
                        <div style={{ marginTop: 10, fontSize: 16 }}>Retorno Financeiro (%)</div>
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "center",
                                marginTop: 20,
                            }}
                        >
                            <div
                                style={{
                                    color: "#fff",
                                    backgroundColor: "#006d2e",
                                    width: 300,
                                    paddingTop: 8,
                                    paddingBottom: 8,
                                }}
                            >
                                Retorno Total: {FormatUtils.formatarValorTela(retornoFinanceiro || 0, 2)}%
                            </div>
                        </div>
                    </div>
                    <div style={{ width: "85vw", overflowX: "auto" }}>
                        <div
                            id="graficoAnaliseRetornoFinanceiroPercentualMensal"
                            style={{
                                width: "100%",
                                minWidth: "890px",
                                height: 400,
                                marginTop: -20,
                            }}
                        ></div>
                    </div>
                </ContentCardBody> */}
                <ContentCardBody
                    style={{
                        backgroundColor: props.backgroundColor || "#30303d",
                        textAlign: "center",
                    }}
                >
                    <div id="tituloGraficoAnaliseRetornoFinanceiroValorAnual" style={{ color: props.fontColor || "#fff", textAlign: "center" }}>
                        <div style={{ marginTop: 10, fontSize: 16 }}>Análise do Retorno Financeiro (R$)</div>
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "center",
                                marginTop: 20,
                            }}
                        >
                            <div
                                style={{
                                    color: "#fff",
                                    backgroundColor: "#006d2e",
                                    width: 300,
                                    paddingTop: 8,
                                    paddingBottom: 8,
                                }}
                            >
                                Retorno Total: {FormatUtils.formatarValorTela(retornoFinanceiro || 0, 2)}%
                            </div>
                        </div>
                    </div>
                    <div style={{ width: "85vw", overflowX: "auto" }}>
                        <div
                            id="graficoAnaliseRetornoFinanceiroValorAnual"
                            style={{
                                width: "100%",
                                minWidth: "890px",
                                height: 400,
                                marginTop: -20,
                            }}
                        ></div>
                    </div>
                </ContentCardBody>
            </ContentCard>
        </div>
    );
}
